import {
  Box,
  Checkbox,
  CrudMode,
  DataDisplay,
  formatBoolean,
  formatDate,
  formatMinutes,
  GeneralValidationErrors,
  Grid,
  GridBreak,
  MultiDatePicker,
  pn,
  Stack,
  Switch,
  Typography,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { DatePicker, DurationInput, Input, Select } from "components";
import {
  ContributorEmployment,
  ContributorMatch,
  ContributorMatchEditResource,
  ContributorMatchViewResource,
} from "generated/openApi";

import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";

export const BasicInfo = (props: {
  crudMode: CrudMode;
  match: ContributorMatch;
  setMatch: (value: ContributorMatch) => void;
  viewResource: ContributorMatchViewResource;
  editResource: ContributorMatchEditResource;
  validationErrors: ValidationErrors | string[];
  hideContributor?: boolean | undefined;
  hideEmployment?: boolean | undefined;
  canSendUnallowableEmail?: boolean;
  willSendUnallowableEmail?: boolean;
  setWillSendUnllowableEmail?: (value: boolean) => void;
}) => {
  const { breakpointsRef, setBreakpoints, smallFieldBreakpoints, mediumFieldBreakpoints, largeFieldBreakpoints, fullFieldBreakpoints } =
    useElementBreakpoints();
  const [selectEmployments, setSelectEmployments] = useState<ContributorEmployment[]>();
  const properties = props.viewResource.properties || [];
  const generationValidationErrors = props.validationErrors[pn<ContributorMatch>("generalValidationErrors")];

  useEffect(() => {
    if (props.match && props.match.contributorId > 0) {
      getSelectEmployments(props.match.contributorId);
    }
  }, [props.match.contributorId]);

  const getSelectEmployments = (contributorId: number) => {
    ApiService.contributorMatches.getContributorEmployments(contributorId).then((response) => {
      setSelectEmployments(response);

      // auto set employment if its not already selected
      if (!props.match.contributorEmploymentId && response && response.length >= 1) {
        props.setMatch(
          produce(props.match, (draft) => {
            draft.contributorEmploymentId = response[0].id;
          })
        );
      }
    });
  };

  const isCreatingBulkField = () => {
    const propertyName = pn<ContributorMatch>("isCreatingBulk");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Switch
        label={property?.displayName}
        checked={props.match.isCreatingBulk}
        onChange={(e) =>
          props.setMatch(
            produce(props.match, (draft) => {
              draft.isCreatingBulk = e.target.checked;
            })
          )
        }
      />
    );
  };

  const creatingBulkDatesField = () => {
    const propertyName = pn<ContributorMatch>("creatingBulkDates");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <MultiDatePicker
        label={property?.displayName}
        required={property?.required}
        errors={props.validationErrors[propertyName]}
        value={props.match.creatingBulkDates}
        onChange={(value) =>
          props.setMatch(
            produce(props.match, (draft) => {
              draft.creatingBulkDates = value;
            })
          )
        }
      />
    );
  };

  const contributorField = () => {
    const propertyName = pn<ContributorMatch>("contributorId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {props.match.contributor && (
          <>
            {props.match.contributor.lastName}, {props.match.contributor.firstName}
          </>
        )}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.match.contributor}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, value) => {
          props.setMatch(
            produce(props.match, (draft) => {
              draft.contributorId = value?.id;
              draft.contributor = value;
              draft.contributorEmploymentId = undefined;
            })
          );
          setSelectEmployments(undefined);
        }}
        fetchFunction={(searchString: string) => ApiService.search.contributors(searchString)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const contributorEmploymentField = () => {
    const propertyName = pn<ContributorMatch>("contributorEmploymentId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {props.match.contributorEmployment && (
          <Stack direction="row" alignItems="center" spacing="sm">
            <Stack direction="row">
              <Typography>{props.match.contributorEmployment.partner?.name}</Typography>
              {props.match.contributorEmployment.subPartner && (
                <Typography>: {props.match.contributorEmployment.subPartner?.name}</Typography>
              )}
            </Stack>
            <Typography variant="caption">
              ({formatDate(props.match.contributorEmployment.startDate)}
              <> - </>
              {props.match.contributorEmployment.endDate
                ? formatDate(props.match.contributorEmployment.endDate)
                : "Active"}
              )
            </Typography>
          </Stack>
        )}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={selectEmployments?.find((x) => x.id === props.match.contributorEmploymentId)}
        options={selectEmployments}
        getOptionLabel={(option) =>
          `${option.partner?.name}${option.subPartner ? `: ${option.subPartner.name}` : ""} (${formatDate(
            option.startDate
          )} - ${option.endDate ? formatDate(option.endDate) : "Active"})`
        }
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Stack>
              <Stack direction="row">
                <Typography>{option.partner?.name}</Typography>
                {option.subPartner && <Typography>: {option.subPartner.name}</Typography>}
              </Stack>

              <Typography variant="caption">
                ({formatDate(option.startDate)}
                <> - </>
                {option.endDate ? formatDate(option.endDate) : "Active"})
              </Typography>
            </Stack>
          </Box>
        )}
        onChange={(e, value) =>
          props.setMatch(
            produce(props.match, (draft) => {
              draft.contributorEmploymentId = value?.id;
            })
          )
        }
      />
    );
  };

  const dateField = () => {
    const propertyName = pn<ContributorMatch>("date");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.match.date)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.match.date}
        onChange={(value) =>
          props.setMatch(
            produce(props.match, (draft) => {
              draft.date = value;
            })
          )
        }
        disabled={props.match.isCreatingBulk}
      />
    );
  };

  const serviceField = () => {
    const propertyName = pn<ContributorMatch>("serviceId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.match.service?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.services?.find((x) => x.id === props.match.serviceId)}
        options={props.editResource.services}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setMatch(
            produce(props.match, (draft) => {
              draft.serviceId = value?.id;
            })
          )
        }
      />
    );
  };

  const durationInMinutesField = () => {
    const propertyName = pn<ContributorMatch>("durationInMinutes");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatMinutes(props.match.durationInMinutes)}</DataDisplay>
    ) : (
      <DurationInput
        label={property?.displayName}
        required={property?.required}
        errors={props.validationErrors[propertyName]}
        value={props.match.durationInMinutes}
        onChange={(value) =>
          props.setMatch(
            produce(props.match, (draft) => {
              draft.durationInMinutes = value!;
            })
          )
        }
      />
    );
  };

  const milesField = () => {
    const propertyName = pn<ContributorMatch>("miles");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.match.miles?.toFixed(2)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.match.miles}
        onChange={(e) =>
          props.setMatch(
            produce(props.match, (draft) => {
              draft.miles = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const reviewField = () => {
    const propertyName = pn<ContributorMatch>("reviewId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.match.review?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.matchReviews?.find((x) => x.id === props.match.reviewId)}
        options={props.editResource.matchReviews}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setMatch(
            produce(props.match, (draft) => {
              draft.reviewId = value?.id;
            })
          )
        }
        disabled={props.editResource.canEditReview === false}
      />
    );
  };

  const isMatchField = () => {
    const propertyName = pn<ContributorMatch>("isMatch");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.match.isMatch)}</DataDisplay>
    ) : (
      <Checkbox
        label={property?.displayName}
        checked={props.match.isMatch}
        onChange={(e) =>
          props.setMatch(
            produce(props.match, (draft) => {
              draft.isMatch = e.target.checked;
            })
          )
        }
      />
    );
  };

  const sendUnallowableEmailField = () => (
    <Switch
      label={`Upon saving, send email to the user who created this Time & Efforts indicating that the Status has been changed to Unallowable`}
      checked={props.willSendUnallowableEmail}
      onChange={(e) => props.setWillSendUnllowableEmail?.(e.target.checked)}
    />
  );

  const noteField = () => {
    const propertyName = pn<ContributorMatch>("note");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName} type="textarea">
        {props.match.note}
      </DataDisplay>
    ) : (
      <Input
        multiline
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.match.note}
        onChange={(e) =>
          props.setMatch(
            produce(props.match, (draft) => {
              draft.note = e.target.value;
            })
          )
        }
      />
    );
  };

  const staffField = () => {
    const propertyName = pn<ContributorMatch>("staffId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {props.match.staff && (
          <>
            {props.match.staff?.lastName}, {props.match.staff?.firstName}
          </>
        )}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.staffs?.find((x) => x.id === props.match.staffId)}
        options={props.editResource.staffs}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, value) =>
          props.setMatch(
            produce(props.match, (draft) => {
              draft.staffId = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      {generationValidationErrors && (
        <Grid item xs={smallFieldBreakpoints}>
          <GeneralValidationErrors errors={generationValidationErrors} />
        </Grid>
      )}

      {props.match.id === 0 && (
        <Grid item xs={smallFieldBreakpoints}>
          {isCreatingBulkField()}
        </Grid>
      )}

      <Grid item xs={smallFieldBreakpoints}>
        {isMatchField()}
      </Grid>

      <GridBreak />

      {!props.match.isCreatingBulk && (
        <Grid item xs={smallFieldBreakpoints}>
          {dateField()}
        </Grid>
      )}

      {props.match.isCreatingBulk && (
        <Grid item xs={largeFieldBreakpoints}>
          {creatingBulkDatesField()}
        </Grid>
      )}

      {!props.hideContributor && (
        <>
          <Grid item xs={mediumFieldBreakpoints}>
            {contributorField()}
          </Grid>
        </>
      )}

      {!props.hideEmployment && (
        <>
          <Grid item xs={mediumFieldBreakpoints}>
            {contributorEmploymentField()}
          </Grid>
        </>
      )}

      <Grid item xs={largeFieldBreakpoints}>
        {serviceField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {durationInMinutesField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {milesField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {reviewField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {staffField()}
      </Grid>

      {props.canSendUnallowableEmail && (
        <Grid item xs={fullFieldBreakpoints}>
          {sendUnallowableEmailField()}
        </Grid>
      )}

      <Grid item xs={fullFieldBreakpoints}>
        {noteField()}
      </Grid>
    </Grid>
  );
};
